import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import api, { logoutUser } from './services/api';

import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import UsersPage from './pages/UsersPage';
import UserDevicesPage from './pages/UserDevicesPage';
import OuvrierPage from './pages/OuvrierPage';
import PointagePage from './pages/PointagePage';
import PointageJournalierPage from './pages/PointageJournalierPage';
import ChantierPage from './pages/ChantierPage';
import AffectationsPage from './pages/AffectationsPage';
import ResumePointagePage from './pages/ResumePointagePage';
import RapportChantierPage from './pages/RapportChantierPage';
import PaieMoisPage from './pages/PaieMoisPage';
import Chatbox from './components/Chatbox';
import AffectationsDepartementPage from './pages/AffectationsDepartementPage';
import MaterielPage from './pages/MaterielPage';
import PointageMaterielPage from './pages/PointageMaterielPage';
import CiterneGasoilPage from './pages/CiterneGasoilPage';
import PointageMaterielListePage from './pages/PointageMaterielListePage';
import BdcReparationPage from './pages/BdcReparationPage';
import MaterielReparationsPage from './pages/MaterielReparationsPage';
import VidangePage from './pages/VidangePage';
import StcPage from './pages/StcPage';
import DashboardTechniquePage from './pages/dashboard';
import FichePage from './pages/dashboard/FichePage';
import BudgetRealisationPage from './pages/BudgetRealisationPage';
import ArticlesSuivisPage from './pages/ArticlesSuivisPage';
import BdcSuivisPage from './pages/BdcSuivisPage';
import OutilsComptabilitePage from './pages/OutilsComptabilitePage';
import DecisionnelPage from './pages/DecisionnelPage';
import PointJourPage from './pages/pointJour/PointJourPage';
import RapportPartagePage from './pages/pointJour/RapportPartagePage';
import BienTranchePage from './pages/BienTranchePage';
import KpiAdminPage from './pages/KpiAdminPage';
import TelephonePage from './pages/TelephonePage';
import ParcInformatiquePage from './pages/ParcInformatiquePage';
import AbonnementsPage from './pages/AbonnementsPage';
import { canAccess } from './utils/canAccess';
import { saveRedirect } from './utils/redirectAfterLogin';
import { computeAidActive } from './utils/aidModeActive';
import AidCornerOverlay from './components/AidMubarak/AidCornerOverlay';
import AidSettingsPanel from './components/AidMubarak/AidSettingsPanel';

const PAGE_TITLES = {
  '/login':                    'Connexion',
  '/':                         'Accueil',
  '/dashboard':                'Dashboard technique',
  '/decisionnel':              'Décisionnel',
  '/point-jour':               'Point du jour',
  '/budget-realisation':       'Budget & Réalisation',
  '/rapport-chantier':         'Rapport Chantier',
  '/paie-mois':                'Paie du Mois',
  '/resume-pointage':          'Résumé Pointage',
  '/ouvriers':                 'Ouvriers',
  '/pointages':                'Pointages',
  '/pointage-journalier':      'Pointage Journalier',
  '/materiels':                'Matériels',
  '/materiels/reparations':    'Réparations',
  '/pointage-materiels':       'Pointage Matériel',
  '/pointage-materiels-liste': 'Liste Pointage Matériel',
  '/vidanges':                 'Vidanges',
  '/stc':                      'Solde de Tout Compte',
  '/citernes-gasoil':          'Citernes Gasoil',
  '/bdc-reparations':          'BDC Réparations',
  '/articles-suivis':          'Articles Critiques',
  '/bdc-suivis':               'Suivi BDC',
  '/outils-comptabilite':      'Outils Comptabilité',
  '/telephones':               'Téléphones',
  '/parc-informatique':        'Parc Informatique',
  '/abonnements':              'Abonnements',
  '/users':                    'Utilisateurs',
  '/user-devices':             'Appareils',
  '/kpi-admin':                'KPI Admin',
  '/bien-tranches':            'Coût par tranche',
  '/chantiers':                'Chantiers',
  '/affectations':             'Affectations',
  '/affectations-departements':'Accès Départements',
};

function TitleUpdater() {
  const location = useLocation();
  useEffect(() => {
    const label = PAGE_TITLES[location.pathname] || (location.pathname.startsWith('/rapport/') ? 'Rapport' : null);
    document.title = label ? `${label} — ShemsPaie` : 'ShemsPaie';
  }, [location.pathname]);
  return null;
}

// ✅ Config globale Axios
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  res => res,
  err => {
    if (err?.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user_privileges');
      localStorage.removeItem('user_departements');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

function App() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [aidSettings, setAidSettings] = useState(null);

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch {
      // ignore network errors — proceed with local cleanup
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user_privileges');
    setUser(null);

    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  };

  // Fetch Aid settings (endpoint public — accessible sans token)
  useEffect(() => {
    api.get('/settings/aid')
      .then(r => setAidSettings(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || isTokenExpired(token)) {
      saveRedirect(window.location.pathname);
      handleLogout();
      setAuthReady(true);
      return;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const departements = JSON.parse(localStorage.getItem('user_departements') || '[]');
      const privileges = JSON.parse(localStorage.getItem('user_privileges') || '[]');

      if (!payload?.id || !payload?.name || !payload?.role) {
        handleLogout();
        setAuthReady(true);
        return;
      }

      setUser({
        id: payload.id,
        name: payload.name,
        login: payload.login,
        role: payload.role,
        departements,
        privileges
      });
    } catch {
      console.error('❌ Token invalide');
      handleLogout();
    }
    setAuthReady(true);
  }, []);

  if (!authReady) return null;

  const aidActive = computeAidActive(aidSettings);

  return (
    <ErrorBoundary>
    <Router>
      <TitleUpdater />
      <div>
        <Navbar user={user} onLogout={handleLogout} aidActive={aidActive} />

        <Routes>
          <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage setUser={setUser} aidActive={aidActive} />} />
          <Route path="/" element={
            user ? <HomePage user={user} /> : <Navigate to="/login" />
          } />

          {/* ✅ DASHBOARD TECHNIQUE (v2 à onglets) + fiches détaillées */}
          <Route
            path="/dashboard"
            element={
              canAccess(user, 'dashboard')
                ? <DashboardTechniquePage />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />
          {/* Ancien dashboard supprimé : les anciens liens/favoris ouvrent le nouveau */}
          <Route path="/dashboard-ancien" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/fiche/:type/:id"
            element={
              canAccess(user, 'dashboard')
                ? <FichePage />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          {/* ✅ Cienco Dashboard */}
          <Route
            path="/budget-realisation"
            element={
              canAccess(user, 'budget-realisation')
                ? <BudgetRealisationPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          {/* ✅ RAPPORT CHANTIER */}
          <Route
            path="/rapport-chantier"
            element={
              canAccess(user, 'rapport-chantier')
                ? <RapportChantierPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          {/* ✅ PAIE MOIS */}
          <Route
            path="/paie-mois"
            element={
              canAccess(user, 'paie-mois')
                ? <PaieMoisPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          {/* Admin uniquement — pas de privilege_key */}
          <Route path="/users" element={user?.role === 'admin' ? <UsersPage /> : <Navigate to="/" />} />
          <Route path="/user-devices" element={user?.role === 'admin' ? <UserDevicesPage /> : <Navigate to="/" />} />
          <Route path="/kpi-admin" element={user?.role === 'admin' ? <KpiAdminPage /> : <Navigate to="/" />} />
          <Route path="/bien-tranches" element={user?.role === 'admin' ? <BienTranchePage /> : <Navigate to="/" />} />

          <Route
            path="/affectations-departements"
            element={user?.role === 'admin' ? <AffectationsDepartementPage user={user} /> : <Navigate to="/" />}
          />

          <Route
            path="/chantiers"
            element={user?.role === 'admin' ? <ChantierPage user={user} /> : <Navigate to="/" />}
          />

          <Route
            path="/affectations"
            element={user?.role === 'admin' ? <AffectationsPage user={user} /> : <Navigate to="/" />}
          />

          <Route
            path="/resume-pointage"
            element={
              canAccess(user, 'resume-pointage')
                ? <ResumePointagePage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/ouvriers"
            element={
              canAccess(user, 'ouvriers')
                ? <OuvrierPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/pointages"
            element={
              canAccess(user, 'pointages')
                ? <PointagePage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/pointage-journalier"
            element={
              canAccess(user, 'pointage-journalier')
                ? <PointageJournalierPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/materiels"
            element={
              canAccess(user, 'materiels')
                ? <MaterielPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/pointage-materiels"
            element={
              canAccess(user, 'pointage-materiels')
                ? <PointageMaterielPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/pointage-materiels-liste"
            element={
              canAccess(user, 'pointage-materiels-liste')
                ? <PointageMaterielListePage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/vidanges"
            element={
              canAccess(user, 'vidanges')
                ? <VidangePage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/stc"
            element={
              user?.role === 'admin' || canAccess(user, 'stc')
                ? <StcPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/citernes-gasoil"
            element={
              canAccess(user, 'citernes-gasoil')
                ? <CiterneGasoilPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/bdc-reparations"
            element={
              canAccess(user, 'bdc-reparations')
                ? <BdcReparationPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/materiels/reparations"
            element={
              canAccess(user, 'materiels-reparations')
                ? <MaterielReparationsPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/articles-suivis"
            element={
              canAccess(user, 'articles-suivis')
                ? <ArticlesSuivisPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/bdc-suivis"
            element={
              canAccess(user, 'bdc-suivis')
                ? <BdcSuivisPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/outils-comptabilite"
            element={
              canAccess(user, 'comptabilite')
                ? <OutilsComptabilitePage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/telephones"
            element={
              canAccess(user, 'telephones')
                ? <TelephonePage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/parc-informatique"
            element={
              canAccess(user, 'parc-informatique')
                ? <ParcInformatiquePage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/abonnements"
            element={
              canAccess(user, 'abonnement')
                ? <AbonnementsPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />

          <Route
            path="/decisionnel"
            element={
              canAccess(user, 'decisionnel') || user?.role === 'resp_commercial'
                ? <DecisionnelPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />
          {/* Point du jour : vue commerciale (Décisionnel) et/ou technique (Dashboard) */}
          <Route
            path="/point-jour"
            element={
              canAccess(user, 'decisionnel') || canAccess(user, 'dashboard') || user?.role === 'resp_commercial'
                ? <PointJourPage user={user} />
                : <Navigate to={user ? '/' : '/login'} />
            }
          />
          <Route path="/decisionnel/point-jour" element={<Navigate to="/point-jour" replace />} />
          {/* Rapport partagé par lien : tout utilisateur connecté, accès vérifié côté serveur */}
          <Route path="/rapport/:token" element={user ? <RapportPartagePage /> : <Navigate to="/login" />} />
        </Routes>

        {user && <Chatbox user={user} />}
        {aidActive && <AidCornerOverlay />}
        <AidSettingsPanel
          user={user}
          aidSettings={aidSettings}
          onSettingsChange={setAidSettings}
        />
      </div>
    </Router>
    </ErrorBoundary>
  );
}

export default App;